// ============================================================
// chunkUtils.js - shared document chunking logic
//
// Goals:
// - Never drop text. Every character of extracted_text must land in at least
//   one chunk — retrieval can't answer from text the chunker threw away.
//   (Before 2026-09-24, sections under MIN_CHARS were silently discarded, and
//   the financial pattern split before every "Total"/"Balance"/"Income" word,
//   which lost ~33% of the dollar figures in live manager's reports — mostly
//   the totals and running balances homeowners actually ask about.)
// - Keep chunks small enough for reliable retrieval and answer prompts.
// - Preserve legal/HOA section context when documents expose headings.
// - Keep markdown table rows whole, and repeat the report heading + column
//   header on continuation chunks so a number never loses its label.
// ============================================================

const CHUNK_PATTERNS = {
  ccr: /(?=\b(?:ARTICLE|Article)\s+[IVX\d]+|\b(?:SECTION|Section)\s+\d+(?:\.\d+)*|\n\s*\(?\d+\)|\n\s*\([a-z]\))/g,
  bylaw: /(?=\b(?:ARTICLE|Article)\s+[IVX\d]+|\b(?:SECTION|Section)\s+\d+(?:\.\d+)*|\n\s*\(?\d+\)|\n\s*\([a-z]\))/g,
  amendment: /(?=\b(?:WHEREAS|AMENDMENT|ARTICLE|Article|SECTION|Section)\b|\n\s*\(?\d+\))/g,
  resolution: /(?=\b(?:WHEREAS|NOW THEREFORE|BE IT RESOLVED|RESOLVED|SECTION|Section)\b)/g,
  minutes: /(?=\b(?:Motion|MOTION|Resolved|RESOLVED|Agenda Item|ACTION ITEM|APPROVED|VOTE|Date):|\n\s*\d+\.\s+[A-Z])/g,
  // Financial packets are page-structured reports (balance sheet, P&L, bank
  // statement, invoices). Split on page boundaries only — never on line-item
  // words like "Total". Headings stay attached to the table below them so
  // splitTable() can carry them onto continuation chunks.
  financial: /(?=\n-{3,}\n|\f)/g,
  contract: /(?=\b(?:Section|SECTION)\s+\d+(?:\.\d+)*|\b(?:WHEREAS|NOW THEREFORE|Party|Clause)\b|\n\s*\d+\.\s+[A-Z])/g,
  legal: /(?=\b(?:Section|SECTION)\s+\d+(?:\.\d+)*|\b(?:WHEREAS|NOW THEREFORE|Article|ARTICLE)\b|\n\s*\d+\.\s+[A-Z])/g,
  notice: /(?=\n\s*\n|\.\s{2,})/g,
  insurance: /(?=\b(?:Section|SECTION)\s+\d+(?:\.\d+)*|\n\s*\d+\.\s+[A-Z]|\n\s*\n)/g,
  other: /(?=\n\s*\n|\.\s{2,})/g,
};

const LEGAL_CATEGORIES = new Set(['ccr', 'bylaw', 'amendment', 'legal', 'contract', 'resolution']);
const FINANCIAL_CATEGORIES = new Set(['financial', 'insurance']);

const LEGAL_MAX_CHARS = 2200;
const STANDARD_MAX_CHARS = 1800;
const FINANCIAL_MAX_CHARS = 1600;
const OVERLAP_CHARS = 240;
const MIN_CHARS = 180;
// Carried table context (headings + column header) is capped so it can't
// crowd out the rows it's there to label.
const MAX_CONTEXT_CHARS = 400;

// Governing documents split at every sub-item ("(a)", "(1)", "d)"), so a
// sub-item chunk used to lose its parent heading: CC&Rs Phase 1 "d) No
// building on any lot shall have metal exterior siding or roofing" was a
// 309-char chunk with no "Section 8: Building Standards" and no document
// name, and ranked below ACC-procedure chunks for metal-roof questions
// (2026-09-27). Each chunk in these categories now starts with a context line
// naming the document, article and section it belongs to.
const SECTION_CONTEXT_CATEGORIES = new Set(['ccr', 'bylaw', 'amendment']);
// A heading needs ":" "." "</u>" or end-of-line right after the number, so
// mid-sentence references ("in accordance with Section 11 of this Article",
// "Article II of the Declaration") are not treated as headings.
const ARTICLE_HEADING = /^(?:<u>)?\**\s*(?:ARTICLE|Article)\s+(?:[IVX]+|\d+)\s*(?:<\/u>|\*|$|[:.—-])/;
const SECTION_HEADING = /^(?:<u>)?\**\s*(?:SECTION|Section)\s+\d+(?:\.\d+)*\s*(?:[:.]|<\/u>)/;
const MAX_LABEL_CHARS = 90;
// A governing-document section that starts with its own heading and has at
// least this much text is a real clause and keeps its own chunk, even when it
// is shorter than MIN_CHARS. Before, CC&Rs Phase 1 §13 Lot Appearance and §14
// Setback lines (≈130 chars each) were packed into §12 Recreational Vehicles,
// so setback questions retrieved a chunk that was mostly about parking.
const MIN_OWN_SECTION_CHARS = 100;

const TABLE_ROW = /^\s*\|.*\|\s*$/;
const TABLE_SEPARATOR = /^\s*\|?(\s*:?-{3,}:?\s*\|)+\s*:?-*:?\s*\|?\s*$/;
const HEADING = /^#{1,6}\s+\S/;

function normalizeText(text) {
  return String(text || '')
    .replace(/\r\n/g, '\n')
    .replace(/[ \t]+/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function getMaxChars(category) {
  if (LEGAL_CATEGORIES.has(category)) return LEGAL_MAX_CHARS;
  if (FINANCIAL_CATEGORIES.has(category)) return FINANCIAL_MAX_CHARS;
  return STANDARD_MAX_CHARS;
}

function getPattern(category) {
  return CHUNK_PATTERNS[category] || CHUNK_PATTERNS.other;
}

function extractSectionHeader(text, fallbackIndex) {
  const lines = text.split('\n').map((line) => line.trim()).filter(Boolean);
  const markdownHeading = lines.find((line) => HEADING.test(line));
  const firstLine = lines[0] || '';
  const headingMatch = firstLine.match(/^(ARTICLE\s+[IVX\d]+[^\n]{0,120}|Article\s+[IVX\d]+[^\n]{0,120}|SECTION\s+\d+(?:\.\d+)*[^\n]{0,120}|Section\s+\d+(?:\.\d+)*[^\n]{0,120}|WHEREAS[^\n]{0,80}|AMENDMENT[^\n]{0,100}|Motion:[^\n]{0,100}|Resolved:[^\n]{0,100}|Agenda Item[^\n]{0,100}|ACTION ITEM:[^\n]{0,100}|VOTE:[^\n]{0,100})/);
  if (headingMatch) return headingMatch[1].trim();
  if (markdownHeading) return markdownHeading.replace(/^#+\s*/, '').slice(0, 120);
  return fallbackIndex === 0 ? 'Opening section' : null;
}

function stripMarkup(text) {
  return text
    .replace(/<sup>.*?<\/sup>/gi, '')
    .replace(/<\/?u>/gi, '')
    .replace(/\*+/g, '')
    .replace(/(^|\s)#+(?=\s)/g, '$1')
    .replace(/\s+/g, ' ')
    .trim();
}

// "Section 8: Building Standards:</u> The following…" -> "Section 8: Building Standards"
// "Section 6. Nonpayment of assessments.</u> Any…"   -> "Section 6. Nonpayment of assessments"
function sectionTitle(firstLine) {
  const underlined = firstLine.split(/<\/u>/i)[0];
  const raw = stripMarkup(underlined);
  const cut = raw.length > MAX_LABEL_CHARS ? `${raw.slice(0, MAX_LABEL_CHARS).replace(/\s+\S*$/, '')}…` : raw;
  return cut.replace(/[\s:.]+$/, '');
}

// "ARTICLE II</u>" followed by "<u>ARCHITECTURAL CONTROL</u>" -> "ARTICLE II ARCHITECTURAL CONTROL"
function articleTitle(sectionText) {
  const [first = '', second = ''] = sectionText.split('\n').map((l) => l.trim()).filter(Boolean);
  const match = stripMarkup(first).match(/^(ARTICLE|Article)\s+([IVX]+|\d+)/);
  const head = match ? `${match[1].toUpperCase()} ${match[2]}` : stripMarkup(first).slice(0, 40);
  const next = stripMarkup(second);
  const nextIsTitle = next.length > 0 && next.length <= 60 && next === next.toUpperCase()
    && /[A-Z]/.test(next) && !SECTION_HEADING.test(second);
  return nextIsTitle ? `${head} ${next}` : head;
}

// Labels each section with the document/article/section it belongs to. A
// section that starts with its own Section heading only needs the document and
// article; sub-items and continuations also get the parent section.
function labelSections(sections, docTitle) {
  let article = null;
  let section = null;
  return sections.map((s) => {
    const firstLine = s.text.split('\n')[0].trim();
    let startsWithHeading = false;
    if (ARTICLE_HEADING.test(firstLine)) {
      article = articleTitle(s.text);
      section = null;
      startsWithHeading = true;
    } else if (SECTION_HEADING.test(firstLine)) {
      section = sectionTitle(firstLine);
      startsWithHeading = true;
    }
    const parts = [docTitle, article, startsWithHeading ? null : section].filter(Boolean);
    return {
      ...s,
      label: parts.length ? `[${parts.join(' · ')}]` : null,
      parentSection: section,
      ownHeading: startsWithHeading,
    };
  });
}

// A single, unmerged governing-document section that opens with its own
// heading and carries real clause text. ownHeading is only ever set for
// SECTION_CONTEXT_CATEGORIES, so packing elsewhere is unchanged.
function isStandaloneClause(piece) {
  return Boolean(piece.ownHeading) && !piece.merged && piece.text.length >= MIN_OWN_SECTION_CHARS;
}

// In governing documents the split pattern also cuts at mid-sentence
// references ("…except in accordance with | Section 11 of this Article"), and
// extractSectionHeader would report that fragment as the heading. Use the
// tracked parent section unless the chunk really starts with a heading.
function chunkHeader(chunk, index) {
  // parentSection is only ever set for SECTION_CONTEXT_CATEGORIES.
  const firstLine = chunk.text.split('\n')[0].trim();
  const startsWithHeading = ARTICLE_HEADING.test(firstLine) || SECTION_HEADING.test(firstLine);
  if (startsWithHeading || !chunk.parentSection) return extractSectionHeader(chunk.text, index);
  return chunk.parentSection;
}

function isDefinitionLike(text) {
  return /\b(shall mean|means|definition|defined as|is defined)\b/i.test(text);
}

function isTableHeavy(text) {
  return text.split('\n').filter((line) => TABLE_ROW.test(line)).length >= 3;
}

function findSplitPoint(text, maxChars) {
  if (text.length <= maxChars) return text.length;

  const windowStart = Math.max(MIN_CHARS, maxChars - 450);
  const window = text.slice(windowStart, maxChars + 1);
  const sentenceMatch = [...window.matchAll(/[.!?]\s+/g)].pop();
  if (sentenceMatch) return windowStart + sentenceMatch.index + sentenceMatch[0].length;

  const paragraphIndex = text.lastIndexOf('\n\n', maxChars);
  if (paragraphIndex >= MIN_CHARS) return paragraphIndex + 2;

  const lineIndex = text.lastIndexOf('\n', maxChars);
  if (lineIndex >= MIN_CHARS) return lineIndex + 1;

  const spaceIndex = text.lastIndexOf(' ', maxChars);
  if (spaceIndex >= MIN_CHARS) return spaceIndex + 1;

  return maxChars;
}

// Prose splitter with overlap. Unlike the pre-2026-09-24 version, the final
// remainder is always emitted — short tails used to be dropped.
function splitProse(sectionText, sectionStart, maxChars) {
  const pieces = [];
  let cursor = 0;

  while (cursor < sectionText.length) {
    const remaining = sectionText.slice(cursor).trimStart();
    cursor += sectionText.slice(cursor).length - remaining.length;
    if (!remaining) break;

    const splitAt = findSplitPoint(remaining, maxChars);
    const pieceText = remaining.slice(0, splitAt).trim();
    if (pieceText) {
      pieces.push({ text: pieceText, start: sectionStart + cursor, end: sectionStart + cursor + splitAt });
    }

    if (splitAt >= remaining.length) break;
    cursor += Math.max(splitAt - OVERLAP_CHARS, 1);
  }

  return pieces;
}

// Line-based splitter for table-heavy sections. Never cuts a row in half, and
// each continuation piece is prefixed with the headings and table column
// header that were in effect where it starts, so "9,210.00" still reads as
// "Road Repair, Jul '25 - Feb 26, Profit & Loss Budget vs. Actual".
function splitTable(sectionText, sectionStart, maxChars) {
  const lines = sectionText.split('\n');
  const pieces = [];
  let headings = [];
  let lastLineWasHeading = false;
  let tableHeader = null;
  let current = [];
  let currentLen = 0;
  let currentStart = sectionStart;
  let offset = sectionStart;

  const contextFor = () => {
    const parts = [...headings];
    if (tableHeader) parts.push(tableHeader.join('\n'));
    const ctx = parts.join('\n');
    return ctx.length > MAX_CONTEXT_CHARS ? ctx.slice(ctx.length - MAX_CONTEXT_CHARS) : ctx;
  };

  const flush = (nextStart) => {
    const text = current.join('\n').trim();
    if (text) pieces.push({ text, start: currentStart, end: nextStart });
    current = [];
    currentLen = 0;
    currentStart = nextStart;
  };

  lines.forEach((line, i) => {
    const lineLen = line.length + 1;

    if (current.length > 0 && currentLen + lineLen > maxChars) {
      flush(offset);
      const ctx = contextFor();
      // Don't repeat context if this line *is* the start of new context.
      if (ctx && !HEADING.test(line) && !(TABLE_ROW.test(line) && TABLE_SEPARATOR.test(lines[i + 1] || ''))) {
        current.push(ctx);
        currentLen += ctx.length + 1;
      }
    }

    if (HEADING.test(line)) {
      headings = lastLineWasHeading ? [...headings, line] : [line];
      tableHeader = null;
    } else if (TABLE_ROW.test(line) && TABLE_SEPARATOR.test(lines[i + 1] || '')) {
      tableHeader = [line, lines[i + 1]];
    } else if (line.trim() && !TABLE_ROW.test(line) && !TABLE_SEPARATOR.test(line)) {
      // Prose between tables ends the current table's column context.
      tableHeader = null;
    }
    if (line.trim()) lastLineWasHeading = HEADING.test(line);

    // A single line longer than the budget (rare — e.g. a flattened page) still
    // has to fit somewhere; fall back to the prose splitter for just that line.
    if (lineLen > maxChars) {
      flush(offset);
      splitProse(line, offset, maxChars).forEach((p) => pieces.push(p));
      currentStart = offset + lineLen;
    } else {
      current.push(line);
      currentLen += lineLen;
    }
    offset += lineLen;
  });
  flush(offset);

  return pieces;
}

function splitSection(sectionText, sectionStart, maxChars) {
  if (sectionText.length <= maxChars) {
    return [{ text: sectionText, start: sectionStart, end: sectionStart + sectionText.length }];
  }
  return isTableHeavy(sectionText)
    ? splitTable(sectionText, sectionStart, maxChars)
    : splitProse(sectionText, sectionStart, maxChars);
}

export function chunkDocument(doc) {
  const category = doc.category || 'other';
  const text = normalizeText(doc.extracted_text);
  if (!text) return [];

  const maxChars = getMaxChars(category);
  const pattern = getPattern(category);

  // 1. Split on the category's structural boundaries, keeping offsets.
  //    Empty/whitespace-only parts are the only thing ever skipped.
  const sections = [];
  let cursor = 0;
  for (const part of text.split(pattern)) {
    const start = cursor;
    cursor += part.length;
    const trimmed = part.trim();
    if (trimmed) sections.push({ text: trimmed, start: start + part.indexOf(trimmed) });
  }

  // 2. Oversized sections are split; everything else passes through whole.
  //    Governing documents carry their article/section context onto every
  //    piece (see SECTION_CONTEXT_CATEGORIES).
  const labelled = SECTION_CONTEXT_CATEGORIES.has(category) ? labelSections(sections, doc.title) : sections;
  const pieces = labelled.flatMap((s) => splitSection(s.text, s.start, maxChars)
    .map((p, j) => ({
      ...p,
      label: s.label || null,
      parentSection: s.parentSection || null,
      // Only a section's first piece starts with its own heading.
      ownHeading: j === 0 && Boolean(s.ownHeading),
    })));

  // 3. Merge only fragments too small to stand alone (a single VOTE line, a
  //    lone heading) into a neighbour, rather than discarding them. Sections
  //    of real size keep their own chunk: packing them together diluted
  //    focused clauses like "Section 11: Pets" and cost citations in the
  //    2026-09-24 eval.
  const packed = [];
  let buffer = null;
  for (const piece of pieces) {
    const eitherTooSmall = buffer && (buffer.text.length < MIN_CHARS || piece.text.length < MIN_CHARS);
    // Never merge a headless sub-item/continuation into a chunk from a
    // different section: the merged chunk's context line would mislabel it.
    // A piece that starts with its own heading is self-describing, and a
    // heading-only buffer (e.g. a bare "ARTICLE VII") has no section to
    // mislabel, so both still merge. (Always true outside governing docs.)
    const safeContext = buffer && (buffer.parentSection === piece.parentSection
      || piece.ownHeading || !buffer.parentSection);
    const bothRealClauses = buffer && (buffer.hasClause || isStandaloneClause(buffer)) && isStandaloneClause(piece);
    if (eitherTooSmall && safeContext && !bothRealClauses && buffer.text.length + 1 + piece.text.length <= maxChars) {
      buffer = {
        ...buffer,
        text: `${buffer.text}\n${piece.text}`,
        end: piece.end,
        merged: true,
        hasClause: Boolean(buffer.hasClause || isStandaloneClause(buffer) || isStandaloneClause(piece)),
      };
    } else {
      if (buffer) packed.push(buffer);
      buffer = { ...piece };
    }
  }
  if (buffer) packed.push(buffer);

  // 4. The context line is prepended last. It's added text, never a
  //    replacement, so every character of extracted_text is still in some
  //    chunk and char offsets still point into the original text.
  return packed.map((chunk, i) => ({
    docId: doc.id,
    docTitle: doc.title,
    docCategory: category,
    docPhase: doc.phase_applicability || 'both',
    chunkIndex: i,
    text: chunk.label ? `${chunk.label}\n${chunk.text}` : chunk.text,
    sectionHeader: chunkHeader(chunk, i),
    charOffsetStart: chunk.start,
    charOffsetEnd: chunk.end,
    isDefinition: isDefinitionLike(chunk.text),
  }));
}
